import type { Organization } from './organization';
import type { User } from './user';

// Product status enum matching Prisma schema
export type ProductStatus = 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'DRAFT';

// Pricing factors configuration
export interface PricingFactors {
  agentCommissionRate: number;        // Agent commission percentage (0-1)
  brokerCommissionRate: number;       // Broker commission percentage (0-1)
  underwritingLoading: number;        // Underwriting loading percentage (0-1)
  administrativeFee: number;          // Fixed administrative fee amount
  subsidyRate?: number;               // Government subsidy percentage (0-1)
  minimumPremium?: number;            // Minimum premium amount
  // Note: currency field removed - now defined only in coverageConfig to avoid duplication
}

// Coverage Configuration (supports both v1.4.0 and v1.5.0)
export interface CoverageConfig {
  activitiesOrProducts: string[];    // Agricultural activities or products eligible for coverage
  perils: string[];                  // Weather perils that the product can cover
  areaOptions: string[];             // Geographic areas that can be insured
  coveragePeriodLimits: {
    type: "date_range" | "duration_days";
    dateValidation?: "none" | "pentads"; // Date validation mode: none = any date, pentads = pentad boundaries only
    startDateRange?: {
      earliest: string;
      latest: string;
    };
    endDateRange?: {
      earliest: string;
      latest: string;
    };
    minDurationDays?: number;
    maxDurationDays?: number;
  };
  sumInsuredLimits: {
    min: number;
    max: number;
  };
  
  coverageLevelGlobalLimits: Record<string, StrikeExhaust>; // Per-peril limits structure
  
  currency: string; // Primary currency field for the entire product
  
  cutoffDate?: string; // Cutoff date for payments - last date where payment for this product is allowed (must be in future)
}

// Administrative Unit Loading Factor (v1.4.0)
export interface AdminUnitLoadingFactor {
  adminUnitId: string;    // UUID reference to administrative_units table
  adminUnitName: string;  // Display name for validation
  adminUnitCode: string;  // Administrative code for hierarchy lookup
  adminLevel: number;     // 0=country, 1=province, 2=district, etc.
  factors: Record<string, number>; // Peril -> loading factor mapping
}

// Base types for v1.5.0 schema
export interface MinMax {
  min: number;
  max: number;
}

export type EnforcementType = "default" | "fixed" | "range";

export interface StrikeExhaust {
  strike: MinMax;
  exhaust: MinMax;
}

// New enforcement structure for v1.5.0
export interface PerilWeighting {
  enforcement: EnforcementType;
  weights: Record<string, MinMax>; // peril name -> min/max range
}

export interface CoverageLevelLimits {
  enforcement: EnforcementType;
  limits: Record<string, StrikeExhaust>; // Per-peril limits
}



// Loading Configuration
export interface LoadingConfig {
  taxRate: number;                   // Tax percentage applied to premiums (0-1)
  subsidyRate: number;               // Government/sponsor subsidy percentage (0-1)
  loadingFactors: AdminUnitLoadingFactor[]; // Normalized loading factors array
  perilWeighting: PerilWeighting;
  coverageLevelLimits: CoverageLevelLimits;
}

// Stakeholders ANRP Distribution (part of Product Config v1.4.0+)
export interface StakeholdersAnrpDistribution {
  administration: {
    insurer: {
      organizationId: string;
      name: string;
      percentage: number;
      role: "Insurer";
    };
    myubuntu: {
      organizationId: string; // Fixed: MyUbuntu organization ID
      name: string; // Fixed: "MyUbuntu Ltd"
      percentage: number;
      role: "MyUbuntu";
    };
  };
  underwriters: Array<{
    organizationId: string;
    name: string;
    percentage: number;
    isInsurer?: boolean;
  }>;
  brokers?: Array<{
    organizationId: string;
    name: string;
    percentage: number;
    role: "Broker";
  }>;
  calculatingAgents?: Array<{
    organizationId: string;
    name: string;
    percentage: number;
    role: "Calculating Agent";
  }>;
  otherOrganisations?: Array<{
    organizationId: string;
    name: string;
    percentage: number;
    role: string;
  }>;
}

// Automation configuration for claims processing pipeline
export type AutomationMode = 'manual' | 'automated';

export interface AutomationStageConfig {
  mode: AutomationMode;
}

export interface AutomationConfig {
  assess: AutomationStageConfig;
  approve: AutomationStageConfig;
  queue: AutomationStageConfig;
}

// Product configuration v1.6.0 (with automation config)
export interface ProductConfig {
  _version?: string;
  coverageConfig: CoverageConfig;
  loadingConfig: LoadingConfig;
  stakeholdersAnrpDistribution: StakeholdersAnrpDistribution;
  pricingFactors: PricingFactors;
  automationConfig?: AutomationConfig;
}

// Main Product interface
export interface Product {
  id: string;
  name: string;
  code: string;
  description?: string;
  version?: string;
  tags: string[];
  status: ProductStatus;
  productConfig: ProductConfig;
  quotationScript?: string;
  claimsScript?: string;
  
  // Relationships
  organizationId: string;
  createdBy?: string;
  updatedBy?: string;
  
  // Populated relationships
  organization: Organization;
  createdByUser?: User;
  updatedByUser?: User;
  
  // Statistics (populated by API)
  quotesCount?: number;
  policiesCount?: number;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

// Product filters for search and filtering
export interface ProductFilters {
  status?: ProductStatus;
  organizationId?: string;
  search?: string;
  tags?: string[];
}

// Product creation/update payload
export interface CreateProductRequest {
  name: string;
  code: string;
  description?: string;
  version?: string;
  tags: string[];
  status?: ProductStatus;
  productConfig: ProductConfig;
  quotationScript?: string;
  claimsScript?: string;
}

export interface UpdateProductRequest extends Partial<CreateProductRequest> {
  id: string;
}

// Additional type for update operations without id (for API calls)
export interface UpdateProductData extends Partial<CreateProductRequest> {
  status?: ProductStatus;
}

// API response types
export interface ProductListResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ProductDetailResponse {
  product: Product;
}

// Status-specific product labels
export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  ARCHIVED: 'Archived',
  DRAFT: 'Draft',
};

// Status colors for UI
export const PRODUCT_STATUS_COLORS: Record<ProductStatus, string> = {
  ACTIVE: 'green',
  PAUSED: 'yellow',
  ARCHIVED: 'gray',
  DRAFT: 'blue',
}; 